import React, { useState } from "react";
import {
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  Users,
  DollarSign,
  Gift,
  Key,
  Shield,
  Hash,
  UserCircle,
  Activity,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";

export default function ClientDetailsModal({
  viewModal,
  setViewModal,
  resetPassword,
}) {
  const [passModal, setPassModal] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [error, setError] = useState("");

  if (!viewModal.open) return null;
  const client = viewModal.client;

  const handleSavePassword = async () => {
    if (!newPass || !confirmPass) {
      setError("All fields are required");
      return;
    }
    if (newPass.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (newPass !== confirmPass) {
      setError("Passwords do not match");
      return;
    }

    setError("");
    await resetPassword(client._id, newPass);

    setPassModal(false);
    setNewPass("");
    setConfirmPass("");
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">

          {/* Header */}
          <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 p-8 rounded-t-3xl">
            <button
              onClick={() => setViewModal({ open: false, client: null })}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border-4 border-white/30">
                <UserCircle className="w-12 h-12 text-white" />
              </div>

              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-1">
                  {client.name}
                </h2>
                <p className="text-white/90 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {client.email}
                </p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              <span
                className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 backdrop-blur-sm
                  ${client.status
                    ? "bg-emerald-600/90 text-white"
                    : "bg-red-600/90 text-white"
                  }`}
              >
                <Activity className="w-3.5 h-3.5" />
                {client.status ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="p-8 space-y-8">

            {/* Balance & Referral */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Balance */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-emerald-600 rounded-xl">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-emerald-700">
                    Wallet Balance
                  </span>
                </div>
                <p className="text-3xl font-bold text-emerald-700">
                  ${Number(client.balance).toLocaleString()}
                </p>
              </div>

              {/* Referral */}
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-6 border border-gray-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-gray-700 rounded-xl">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Referral Earnings
                  </span>
                </div>
                <p className="text-3xl font-bold text-gray-800">
                  ${Number(client?.referral?.earnings || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {client?.referral?.referredCount || 0} referrals
                </p>
              </div>
            </div>

            {/* Personal Info */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-3">
                <User className="w-6 h-6 text-gray-700" />
                Personal Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InfoCard icon={Phone} label="Phone Number" value={client.phone || "—"} color="gray" />
                <InfoCard icon={UserCircle} label="Gender" value={client.gender || "—"} color="gray" />
                <InfoCard icon={Briefcase} label="Profession" value={client.profession || "Not specified"} color="gray" />
                <InfoCard icon={Hash} label="Referral Code" value={client.referralCode} color="gray" />
              </div>
            </div>

            {/* Security */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-3">
                <Shield className="w-6 h-6 text-gray-700" />
                Security Information
              </h3>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gray-700 rounded-xl">
                    <Key className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </p>
                    <div className="bg-white px-5 py-4 rounded-xl border border-gray-300 font-mono text-gray-800 break-all">
                      {client.password || "••••••••"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reset Password Button */}
            <button
              onClick={() => setPassModal(true)}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl"
            >
              <RefreshCw className="w-6 h-6" />
              Reset Password
            </button>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {passModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl relative">
            <button
              onClick={() => {
                setPassModal(false);
                setError("");
                setNewPass("");
                setConfirmPass("");
              }}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Reset Password for {client.name}
            </h2>

            {/* New Password */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={show1 ? "text" : "password"}
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-gray-600 outline-none text-base"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShow1(!show1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  {show1 ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={show2 ? "text" : "password"}
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-gray-600 outline-none text-base"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShow2(!show2)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  {show2 ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-xl mb-5 text-sm font-medium">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setPassModal(false);
                  setError("");
                  setNewPass("");
                  setConfirmPass("");
                }}
                className="flex-1 py-3.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePassword}
                className="flex-1 py-3.5 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-900 transition shadow-md"
              >
                Save Password
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* Reusable InfoCard */
function InfoCard({ icon: Icon, label, value, color = "gray" }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-gray-700 rounded-xl">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-sm font-medium text-gray-600">{label}</span>
      </div>
      <p className="font-bold text-gray-800 text-base">{value}</p>
    </div>
  );
}